import type { ShipmentItem, TabItem } from '../types';

export type CustomCellDraftIdentity = {
  reference_number?: string;
  order_num?: string;
  tracking_num?: string;
};

/** Per draft row: Notes values plus enough identity to persist on preset save. */
export type CustomCellDraftEntry = CustomCellDraftIdentity & {
  values: Record<string, string>;
};

/** Per-tab drafts: rowKey -> draft entry */
export type CustomCellDraftStore = Record<
  number,
  Map<string, CustomCellDraftEntry>
>;

type RowIdentity = Pick<ShipmentItem, 'order_num' | 'tracking_num'> & {
  reference_number?: unknown;
};

/**
 * Stable key for matching a report row across search/refetch.
 * Prefer order+tracking; fall back to reference_number (what users often search by).
 * Returns null when no usable identifier is present.
 */
export function buildCustomRowKey(
  item: RowIdentity | null | undefined
): string | null {
  if (!item) return null;
  const order = item.order_num != null ? String(item.order_num).trim() : '';
  const tracking =
    item.tracking_num != null ? String(item.tracking_num).trim() : '';
  if (order || tracking) return `${order}_${tracking}`;

  const reference =
    item.reference_number != null ? String(item.reference_number).trim() : '';
  if (reference) return `ref:${reference}`;

  return null;
}

export function identityFromRow(
  row: RowIdentity | null | undefined
): CustomCellDraftIdentity {
  if (!row) return {};
  const identity: CustomCellDraftIdentity = {};
  if (row.reference_number != null && String(row.reference_number).trim()) {
    identity.reference_number = String(row.reference_number).trim();
  }
  if (row.order_num != null && String(row.order_num).trim()) {
    identity.order_num = String(row.order_num).trim();
  }
  if (row.tracking_num != null && String(row.tracking_num).trim()) {
    identity.tracking_num = String(row.tracking_num).trim();
  }
  return identity;
}

export function getTabDraftMap(
  store: CustomCellDraftStore,
  tabIndex: number
): Map<string, CustomCellDraftEntry> {
  if (!store[tabIndex]) {
    store[tabIndex] = new Map();
  }
  return store[tabIndex];
}

export function setCustomCellDraft(
  store: CustomCellDraftStore,
  tabIndex: number,
  rowKey: string,
  columnKey: string,
  value: string,
  identity?: CustomCellDraftIdentity
): void {
  const map = getTabDraftMap(store, tabIndex);
  const prev = map.get(rowKey) || { values: {} };
  map.set(rowKey, {
    ...prev,
    ...(identity?.reference_number
      ? { reference_number: identity.reference_number }
      : {}),
    ...(identity?.order_num ? { order_num: identity.order_num } : {}),
    ...(identity?.tracking_num ? { tracking_num: identity.tracking_num } : {}),
    values: { ...prev.values, [columnKey]: value },
  });
}

export function scrubCustomColumnFromDrafts(
  store: CustomCellDraftStore,
  tabIndex: number,
  columnKey: string
): void {
  const map = store[tabIndex];
  if (!map) return;
  for (const [rowKey, entry] of map.entries()) {
    if (!(columnKey in entry.values)) continue;
    const nextValues = { ...entry.values };
    delete nextValues[columnKey];
    if (Object.keys(nextValues).length === 0) map.delete(rowKey);
    else map.set(rowKey, { ...entry, values: nextValues });
  }
}

/**
 * Copy custom-column strings from rows into the draft map.
 * Never overwrites a key already present — live edits win over stale row state.
 */
export function seedDraftsFromRows(
  store: CustomCellDraftStore,
  tabIndex: number,
  rows: ShipmentItem[] | undefined | null,
  customKeys: string[]
): void {
  if (!rows?.length || !customKeys.length) return;
  const map = getTabDraftMap(store, tabIndex);
  rows.forEach((row) => {
    const rowKey = buildCustomRowKey(row);
    if (!rowKey) return;
    const prev = map.get(rowKey) || { values: {} };
    const nextValues = { ...prev.values };
    let changed = false;
    customKeys.forEach((key) => {
      // Skip keys the draft map already owns — an in-flight edit is newer
      // than whatever is still sitting on shipmentDataRef.
      if (Object.prototype.hasOwnProperty.call(prev.values, key)) return;
      const value = row[key];
      if (typeof value === 'string') {
        nextValues[key] = value;
        changed = true;
      }
    });
    const identity = identityFromRow(row);
    const identityChanged =
      (!!identity.reference_number &&
        identity.reference_number !== prev.reference_number) ||
      (!!identity.order_num && identity.order_num !== prev.order_num) ||
      (!!identity.tracking_num && identity.tracking_num !== prev.tracking_num);
    if (changed || identityChanged) {
      map.set(rowKey, {
        ...prev,
        ...identity,
        values: nextValues,
      });
    }
  });
}

/**
 * Apply draft custom values onto API rows after search/refetch.
 * Draft map wins; also reapplies every draft key for the row so Notes
 * still restore if the caller’s customKeys list is stale/empty.
 */
export function applyCustomDraftsToRows(
  rows: ShipmentItem[],
  customKeys: string[],
  store: CustomCellDraftStore,
  tabIndex: number
): ShipmentItem[] {
  const existingMap = store[tabIndex];
  if (!customKeys.length && (!existingMap || existingMap.size === 0))
    return rows;
  const map = existingMap ?? getTabDraftMap(store, tabIndex);

  return rows.map((shipment) => {
    const nextRecord: ShipmentItem = { ...shipment };
    const rowKey = buildCustomRowKey(shipment);
    const drafts = rowKey ? map.get(rowKey)?.values : undefined;

    customKeys.forEach((key) => {
      if (drafts && Object.prototype.hasOwnProperty.call(drafts, key)) {
        nextRecord[key] = drafts[key];
      } else if (!Object.prototype.hasOwnProperty.call(nextRecord, key)) {
        nextRecord[key] = '';
      }
    });

    // Belt-and-suspenders: reapply every stored draft key for this row.
    if (drafts) {
      Object.keys(drafts).forEach((key) => {
        nextRecord[key] = drafts[key];
      });
    }

    return nextRecord;
  });
}

function resolveReferenceNumber(
  rowKey: string,
  entry: CustomCellDraftEntry
): string | null {
  if (entry.reference_number?.trim()) return entry.reference_number.trim();
  if (rowKey.startsWith('ref:')) {
    const ref = rowKey.slice(4).trim();
    return ref || null;
  }
  return null;
}

/**
 * Build tab payloads for preset save/update: current rows with drafts applied,
 * plus stub rows for any draft whose shipment is no longer in `tab.data`
 * (e.g. searched away). Backend persists values keyed by reference_number.
 */
export function materializeTabsForPresetSave(
  tabs: TabItem[],
  store: CustomCellDraftStore
): TabItem[] {
  return tabs.map((tab, tabIndex) => {
    const map = store[tabIndex];
    if (!map?.size) return tab;

    const seen = new Set<string>();
    const mergedRows: ShipmentItem[] = (tab.data || []).map((row) => {
      const rowKey = buildCustomRowKey(row);
      if (rowKey) seen.add(rowKey);
      const entry = rowKey ? map.get(rowKey) : undefined;
      if (!entry) return row;
      return { ...row, ...entry.values } as ShipmentItem;
    });

    for (const [rowKey, entry] of map.entries()) {
      if (seen.has(rowKey)) continue;
      const reference_number = resolveReferenceNumber(rowKey, entry);
      if (!reference_number) continue;
      if (!Object.keys(entry.values).length) continue;

      mergedRows.push({
        order_num: entry.order_num || '',
        tracking_num: entry.tracking_num || '',
        reference_number,
        ...entry.values,
      } as unknown as ShipmentItem);
    }

    return { ...tab, data: mergedRows };
  });
}
